import { OfferType } from '@jumpstart/core';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import moment from 'moment';
import React, { Fragment, useState } from 'react';
import { db } from '../../../core/firebase';
import { IConfirmation, IScoutRequest } from '../schemas';
import BookingForm from './BookingForm';
import {QuerySnapshot, QueryDocumentSnapshot, addDoc, collection, DocumentReference, updateDoc, setDoc, doc, deleteDoc } from 'firebase/firestore';

export interface IRequestList {
  requests: QuerySnapshot<IScoutRequest>;
  bookings: QuerySnapshot<IConfirmation>;
}

export default function RequestList(props: IRequestList) {
  const { requests, bookings } = props;
  const [request, setRequest] = useState<QueryDocumentSnapshot<IScoutRequest> | null>(null);
  const [requestDelete, setRequestDelete] = useState<QueryDocumentSnapshot<IScoutRequest> | null>(null);

  // Don't show requests that already have confirmations assigned
  const filterDoc = (doc: QueryDocumentSnapshot<IScoutRequest>): boolean => doc.get('offer.type') === OfferType.SERVICE_LINE && bookings.docs.findIndex(d => d.get(
    'requestId') === doc.id) === -1;

  const handleSubmit = (confirmation: IConfirmation) => {
    if (!request) {
      console.error('No request to pair');
      return;
    }
    // const data: IScoutRequest = request.data();
    const conf: IConfirmation = {
      ...confirmation,
      requestId: request.id
    }
    if (confirmation.id.toLowerCase() === 'unknown') {
      addDoc(collection(db, 'scout9-booking-confirmation'), conf)
        .then((doc: DocumentReference) => {
          return updateDoc(doc, {id: doc.id})
        })
        .then(() => {
          console.log('Confirmation created')
        })
        .catch((err: Error) => {
          console.error(err);
        })
    } else {
      setDoc(doc(db, 'scout9-booking-confirmation', confirmation.id), conf)
        .then(() => {
          console.log('Confirmation saved')
        })
        .catch((err: Error) => {
          console.error(err);
        })
    }
    console.log('Appointment Time:', moment(conf.appointmentTime).format('YYYY-MM-DD[T]hh:00'));
    console.log(conf);
    setRequest(null);
  };

  const handleRemove = () => {
    if (requestDelete) {
      deleteDoc(requestDelete.ref)
        .then(() => {
          console.log('Item deleted');
        })
        .catch((err: Error) => {
          console.error('Failed to delete', err);
        });
    }
    setRequestDelete(null);
  };

  const uidFromRequest = (doc: QueryDocumentSnapshot<IScoutRequest>): { uid: string, dogId: string } => {
    const paths = doc.ref.path.split('/');
    return {
      uid: paths[1],
      dogId: paths[3]
    };
  };

  const buildDefaultConfirmation = (
    data: IScoutRequest,
    doc: QueryDocumentSnapshot<IScoutRequest>
  ): IConfirmation => {
    const { uid, dogId } = uidFromRequest(doc);
    return {
      id: 'UNKNOWN',
      uid: uid,
      dogId: dogId,
      userName: data.payload.userName ? data.payload.userName : null,
      dogName: data.payload.dogName ? data.payload.dogName : null,
      userPhone: data.payload.phone ? data.payload.phone : null,
      userEmail: data.payload.email as any,
      organizationId: data.payload.preferredBusiness ? data.payload.preferredBusiness : 'UNKNOWN',
      requestId: doc.id,
      appointmentTime: moment().add(3, 'hour').startOf('hour').toISOString(),
      created: new Date().toISOString()
    };
  };

  return (
    <Fragment>
      <List dense disablePadding>

        {requests.docs
          .filter(filterDoc)
          .map((doc: QueryDocumentSnapshot<IScoutRequest>, index: number) => {
              const request = doc.data() as IScoutRequest;
              const timestamp = (request.created ? moment(request.created) : moment().subtract(3, 'days')).format(
                'MMM Do YYYY, h:mm a');

              return (
                <ListItem
                  key={doc.id}
                  button
                  onClick={() => {
                    setRequest(doc);
                  }}
                >
                  <ListItemText
                    id={doc.id}
                    primary={`${request.payload.userName}, ${request.payload.dogName} requests ${request.offer.name}`}
                    // primaryTypographyProps={{ style: { 'textDecoration': option.active ? 'none' : 'line-through' } }}
                    secondary={`Created ${timestamp}`}
                    secondaryTypographyProps={{ style: { fontSize: 10 } }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => setRequestDelete(doc)}>
                      <HighlightOffIcon/>
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>);
            }
          )}
      </List>


      <Dialog
        maxWidth="xs"
        open={!!requestDelete}
        onClose={() => setRequestDelete(null)}
      >
        <DialogTitle>Delete Request?</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this request?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setRequestDelete(null)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleRemove} color="primary">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!request}
        onClose={() => setRequest(null)}
        fullScreen
      >
        <Container maxWidth="sm" disableGutters>
        <DialogTitle>Confirmation Form</DialogTitle>
        <Grid container direction="column" alignItems="center" style={{padding: 24}}>
          {request && <BookingForm
            request={request.data() as IScoutRequest}
            onSubmit={handleSubmit}
            confirmation={buildDefaultConfirmation(request.data() as IScoutRequest, request)}
          />}
          <Button variant="contained" color="inherit" style={{ width: '100%' }} onClick={() => setRequest(null)}>Cancel</Button>
        </Grid>
        </Container>
      </Dialog>
    </Fragment>
  );
}
